import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { forkJoin, merge, Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import produce from 'immer';
import { sortBy } from 'lodash-es';
import { ApiService, SentryService } from '../../services';

import {
  RelationshipToInsured,
  mapRelationshipToInsured,
  UsState,
  mapUsState,
  ProductGroupHcpcs,
  InsuranceTypeReportGroups,
  mapInsuranceTypeReportGroups,
  mapProductGroupHcpcs,
} from '../../models';

import {
  MEDICARE_INSURANCE_TYPE,
  MEDICARE_REPL_INSURANCE_TYPE,
  SENSORS_HCPCS_GROUP,
  SETS_RESERVOIRS_HCPCS_GROUP,
  TESTING_SUPPLIES_HCPCS_GROUP,
} from '../../constants';
import { defaultApiStoreIterableData, ApiStoreData } from '../utils';
import { LoadLookupData } from './lookup.actions';

export interface LookupStateModel {
  insuranceGroups: ApiStoreData<InsuranceTypeReportGroups[]>;
  hcpcsGroups: ApiStoreData<ProductGroupHcpcs[]>;
  relationshipsToInsured: ApiStoreData<RelationshipToInsured[]>;
  usStates: ApiStoreData<UsState[]>;
}

@State<LookupStateModel>({
  name: 'lookup',
  defaults: {
    insuranceGroups: defaultApiStoreIterableData,
    hcpcsGroups: defaultApiStoreIterableData,
    relationshipsToInsured: defaultApiStoreIterableData,
    usStates: defaultApiStoreIterableData,
  },
})
@Injectable({ providedIn: 'root' })
export class LookupState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static insuranceGroups(state: LookupStateModel): InsuranceTypeReportGroups[] {
    return state.insuranceGroups.data;
  }

  @Selector()
  static hcpcsGroups(state: LookupStateModel): ProductGroupHcpcs[] {
    return state.hcpcsGroups.data;
  }

  @Selector()
  static hcpcsGroupsIsFinished(state: LookupStateModel): boolean {
    return state.hcpcsGroups.isFinished;
  }

  @Selector()
  static usStates(state: LookupStateModel): UsState[] {
    return state.usStates.data;
  }

  @Selector()
  static relationshipsToInsured(state: LookupStateModel): RelationshipToInsured[] {
    return sortBy(state.relationshipsToInsured.data, 'value');
  }

  fetchInsuranceGroups(ctx: StateContext<LookupStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.insuranceGroups.isLoading = true;
      })
    );

    return forkJoin([
      this.apiService.getInsuranceTypeReportGroups(MEDICARE_INSURANCE_TYPE),
      this.apiService.getInsuranceTypeReportGroups(MEDICARE_REPL_INSURANCE_TYPE),
    ]).pipe(
      tap({
        next: ([medicareResponse, medicareReplResponse]) => {
          const data = [
            mapInsuranceTypeReportGroups(medicareResponse.data),
            mapInsuranceTypeReportGroups(medicareReplResponse.data),
          ];

          this.handleSuccess({ ctx, data, stateKey: 'insuranceGroups' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'insuranceGroups' }))
    );
  }

  fetchHcpcsGroups(ctx: StateContext<LookupStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.hcpcsGroups.isLoading = true;
      })
    );

    return forkJoin([
      this.apiService.getProductGroupHcpcs(SETS_RESERVOIRS_HCPCS_GROUP),
      this.apiService.getProductGroupHcpcs(SENSORS_HCPCS_GROUP),
      this.apiService.getProductGroupHcpcs(TESTING_SUPPLIES_HCPCS_GROUP),
    ]).pipe(
      tap({
        next: ([setsReservoirsResponse, sensorsResponse, testingSuppliesResponse]) => {
          const data = [
            mapProductGroupHcpcs(setsReservoirsResponse.data),
            mapProductGroupHcpcs(sensorsResponse.data),
            mapProductGroupHcpcs(testingSuppliesResponse.data),
          ];

          this.handleSuccess({ ctx, data, stateKey: 'hcpcsGroups' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'hcpcsGroups' }))
    );
  }

  fetchRelationshipsToInsured(ctx: StateContext<LookupStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.relationshipsToInsured.isLoading = true;
      })
    );

    return this.apiService.getRelationshipsToInsured().pipe(
      tap({
        next: (response) => {
          const data = response.data.map((r) => mapRelationshipToInsured(r));

          this.handleSuccess({ ctx, data, stateKey: 'relationshipsToInsured' });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'relationshipsToInsured' })
      )
    );
  }

  fetchUsStates(ctx: StateContext<LookupStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.usStates.isLoading = true;
      })
    );

    return this.apiService.getUsStates().pipe(
      tap({
        next: (response) => {
          const data = response.data.map((s) => mapUsState(s));

          this.handleSuccess({ ctx, data, stateKey: 'usStates' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'usStates' }))
    );
  }

  @Action(LoadLookupData)
  loadLookupData(ctx: StateContext<LookupStateModel>) {
    return merge(
      this.fetchInsuranceGroups(ctx),
      this.fetchHcpcsGroups(ctx),
      this.fetchRelationshipsToInsured(ctx),
      this.fetchUsStates(ctx)
    );
  }
}
